import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Monitor, Smartphone, ArrowRight, Lock } from "lucide-react";
import { CONCEPT_SCREENS, IMAGES } from "../content";import { EASE, Reveal } from "../motion";
import { Section, Wrap, SectionHead } from "./Section";

/* ---------- shared mini-UI primitives ---------- */

const MNav = ({ mode }) => (
  <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#26342E]/10 bg-[#F6F0E6]">
    <span className="font-serif text-sm font-medium text-[#26342E]">Your Organisation</span>
    {mode === "desktop" && (
      <span className="flex items-center gap-4 text-[9px] font-semibold uppercase tracking-[0.14em] text-[#26342E]/60">
        <span>Support</span><span>Learn</span><span>Resources</span><span>About</span>
      </span>
    )}
    <span className="text-[8px] font-bold uppercase tracking-[0.14em] bg-[#26342E] text-[#F6F0E6] px-3 py-1.5 rounded-full">Enquire</span>
  </div>
);

const Chip = ({ children, on = false }) => (
  <span className={`text-[9px] font-semibold px-3 py-1.5 rounded-full border ${on ? "bg-[#26342E] text-[#F6F0E6] border-[#26342E]" : "border-[#26342E]/20 text-[#26342E]/70"}`}>
    {children}
  </span>
);

const MBtn = ({ children, tone = "dark" }) => (
  <span className={`inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.14em] px-4 py-2 rounded-full ${tone === "dark" ? "bg-[#26342E] text-[#F6F0E6]" : "bg-[#B66E50] text-white"}`}>
    {children}
  </span>
);

const Field = ({ label, hint }) => (
  <div>
    <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-[#26342E]/55 mb-1">{label}</p>
    <div className="border border-[#26342E]/15 rounded-lg bg-white/70 px-3 py-2 text-[10px] text-[#26342E]/40">{hint}</div>
  </div>
);

const Screen = ({ children }) => <div className="bg-[#F6F0E6] min-h-full text-[#26342E]">{children}</div>;

/* ---------- the 11 concept screens ---------- */

const SCREENS = {
  home: (mode) => (
    <Screen>
      <MNav mode={mode} />
      <div className="relative h-28 sm:h-36 overflow-hidden">
        <img src={IMAGES.sanctuary} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#26342E]/35" />
        <p className="absolute bottom-3 left-4 sm:left-6 font-serif text-white text-lg sm:text-2xl font-light leading-tight max-w-[16rem]">
          Support that begins with understanding
        </p>
      </div>
      <div className="p-4 sm:p-6">
        <p className="text-[10px] text-[#26342E]/60 mb-2.5">Tell us what brings you here.</p>
        <div className="flex flex-wrap gap-1.5">
          <Chip on>For myself</Chip><Chip>For my child</Chip><Chip>For a school</Chip><Chip>To learn</Chip>
        </div>
        <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-[#26342E]/45 mt-5 mb-2">Featured support</p>
        <div className={`grid ${mode === "desktop" ? "grid-cols-4" : "grid-cols-2"} gap-2`}>
          {["Therapy", "Family support", "School programmes", "Training"].map((t) => (
            <div key={t} className="border border-[#26342E]/10 rounded-xl p-3 bg-white/60">
              <p className="text-[10px] font-semibold">{t}</p>
              <p className="text-[8px] text-[#26342E]/50 mt-1 flex items-center gap-1">Explore <ArrowRight size={8} /></p>
            </div>
          ))}
        </div>
      </div>
    </Screen>
  ),

  discovery: (mode) => (
    <Screen>
      <MNav mode={mode} />
      <div className="p-4 sm:p-6">
        <p className="font-serif text-xl font-light">Therapy and counselling</p>
        <p className="text-[10px] text-[#26342E]/60 mt-1 mb-3">Choose the area closest to what you are carrying.</p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          <Chip on>Anxiety</Chip><Chip>Relationships</Chip><Chip>Grief and loss</Chip><Chip>Trauma</Chip><Chip>Burnout</Chip>
        </div>
        {[
          ["Individual therapy", "One-to-one support, in person or online"],
          ["Couples and relationships", "A structured space to be heard"],
          ["Support for young adults", "Finding footing in changing years"],
        ].map(([t, d]) => (
          <div key={t} className="flex items-center justify-between border-t border-[#26342E]/10 py-3">
            <div>
              <p className="text-[11px] font-semibold">{t}</p>
              <p className="text-[9px] text-[#26342E]/55 mt-0.5">{d}</p>
            </div>
            <ArrowRight size={11} className="text-[#B66E50]" />
          </div>
        ))}
      </div>
    </Screen>
  ),

  practitioner: (mode) => (
    <Screen>
      <MNav mode={mode} />
      <div className={`p-4 sm:p-6 ${mode === "desktop" ? "grid grid-cols-[auto_1fr] gap-5" : ""}`}>
        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-[#6F806E]/40 mb-3 shrink-0">
          <img src={IMAGES.walk} alt="" className="w-full h-full object-cover object-[center_20%] scale-[1.6]" />
        </div>
        <div>
          <p className="font-serif text-xl font-light">Thandi Mokoena</p>
          <p className="text-[9px] text-[#26342E]/60 mt-0.5">Sample profile · Counsellor · Families · Young adults</p>
          <p className="font-serif italic text-[12px] text-[#B66E50] mt-3 leading-snug max-w-xs">
            “My role is to help you feel understood, then help you find a practical way forward.”
          </p>
          <div className={`grid ${mode === "desktop" ? "grid-cols-2" : "grid-cols-1"} gap-x-6 mt-4`}>
            {[["Approach", "Person-centred, practical, unhurried"], ["Areas of support", "Family change, anxiety, identity"], ["Languages", "English, isiZulu, Sesotho"], ["Availability", "Weekdays and Saturday mornings"]].map(([k, v]) => (
              <div key={k} className="border-t border-[#26342E]/10 py-2">
                <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-[#26342E]/45">{k}</p>
                <p className="text-[10px] text-[#26342E]/80 mt-0.5">{v}</p>
              </div>
            ))}
          </div>
          <div className="mt-4"><MBtn>Enquire privately</MBtn></div>
        </div>
      </div>
    </Screen>
  ),

  family: (mode) => (
    <Screen>
      <MNav mode={mode} />
      <div className="p-4 sm:p-6">
        <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-[#B66E50]">Parents and young people</p>
        <p className="font-serif text-xl font-light mt-1.5 max-w-xs">Care explained without clinical distance</p>
        <div className={`grid ${mode === "desktop" ? "grid-cols-3" : "grid-cols-1"} gap-2.5 mt-4`}>
          {["What support is suitable?", "Who will work with my child?", "What happens after I enquire?"].map((q, i) => (
            <div key={q} className="bg-white/60 border border-[#26342E]/10 rounded-xl p-3.5">
              <p className="font-serif italic text-[12px] leading-snug">{q}</p>
              <p className="text-[9px] text-[#26342E]/55 mt-2 leading-relaxed">
                {["Age, temperament and context shape the recommendation, explained in plain words.", "Meet the practitioner, their approach and experience before any commitment.", "A calm outline of the first conversation, costs and what follows."][i]}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-4"><MBtn>Talk to someone first</MBtn></div>
      </div>
    </Screen>
  ),

  schools: (mode) => (
    <Screen>
      <MNav mode={mode} />
      <div className="p-4 sm:p-6">
        <p className="font-serif text-xl font-light max-w-xs">A separate journey for schools and organisations</p>
        <div className={`grid ${mode === "desktop" ? "grid-cols-3" : "grid-cols-1"} gap-2.5 mt-4`}>
          {[["Need", "Learner wellbeing, parent support or staff capability"], ["Proof", "Facilitator credentials, delivery approach and outcomes"], ["Action", "Request a consultation or programme proposal"]].map(([k, v], i) => (
            <div key={k} className="border border-[#26342E]/10 rounded-xl p-3.5 bg-white/60">
              <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-[#6F806E]">0{i + 1} · {k}</p>
              <p className="text-[10px] text-[#26342E]/75 mt-1.5 leading-relaxed">{v}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-2"><MBtn>Request a consultation</MBtn><Chip>Download overview</Chip></div>
      </div>
    </Screen>
  ),

  training: (mode) => (
    <Screen>
      <MNav mode={mode} />
      <div className="p-4 sm:p-6">
        <p className="font-serif text-xl font-light">Training and courses</p>
        <p className="text-[10px] text-[#26342E]/60 mt-1 mb-4">Programmes with requirements and dates up front.</p>
        <div className={`grid ${mode === "desktop" ? "grid-cols-3" : "grid-cols-1"} gap-2.5`}>
          {[["Foundations of supportive conversation", "2 days · In person", "March intake"], ["Supporting young people", "4 evenings · Online", "April intake"], ["Wellbeing for leadership teams", "Half day · On site", "On request"]].map(([t, f, d]) => (
            <div key={t} className="border border-[#26342E]/10 rounded-xl p-3.5 bg-white/60">
              <p className="text-[11px] font-semibold leading-snug">{t}</p>
              <p className="text-[9px] text-[#26342E]/55 mt-1.5">{f}</p>
              <p className="text-[8px] font-bold uppercase tracking-[0.14em] text-[#B66E50] mt-2">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </Screen>
  ),

  resources: (mode) => (
    <Screen>
      <MNav mode={mode} />
      <div className="p-4 sm:p-6">
        <p className="font-serif text-xl font-light">Explore at your own pace</p>
        <div className="flex flex-wrap gap-1.5 mt-3 mb-4">
          <Chip on>All</Chip><Chip>Anxiety</Chip><Chip>Families</Chip><Chip>Schools</Chip><Chip>Workplace</Chip>
        </div>
        {[
          ["5 min read", "A gentle first step"],
          ["For parents", "When a child is struggling"],
          ["Workshop", "Building safer communities"],
          ["Course", "Learning to support others"],
        ].map(([k, t]) => (
          <div key={t} className="flex items-center justify-between border-t border-[#26342E]/10 py-3">
            <div>
              <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-[#6F806E]">{k}</p>
              <p className="text-[11px] font-semibold mt-0.5">{t}</p>
            </div>
            <span className="text-[8px] font-bold uppercase tracking-[0.14em] text-[#B66E50] flex items-center gap-1">Open <ArrowRight size={9} /></span>
          </div>
        ))}
      </div>
    </Screen>
  ),

  events: (mode) => (
    <Screen>
      <MNav mode={mode} />
      <div className="relative h-24 sm:h-28 overflow-hidden">
        <img src={IMAGES.circle} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#26342E]/30" />
        <p className="absolute bottom-2.5 left-4 sm:left-6 font-serif text-white text-base sm:text-lg font-light">Gatherings that build trust</p>
      </div>
      <div className="p-4 sm:p-6">
        <p className="font-serif text-xl font-light">Events and workshops</p>
        <div className="mt-4 space-y-2.5">
          {[["14", "Mar", "An evening for parents of teens", "Cape Town · In person"], ["02", "Apr", "Understanding anxiety, together", "Online circle"], ["26", "Apr", "Staff wellbeing half-day", "Johannesburg · On site"]].map(([d, m, t, v]) => (
            <div key={t} className="flex items-center gap-4 border border-[#26342E]/10 rounded-xl p-3 bg-white/60">
              <div className="text-center w-10 shrink-0">
                <p className="font-serif text-lg leading-none">{d}</p>
                <p className="text-[8px] font-bold uppercase tracking-[0.16em] text-[#B66E50] mt-1">{m}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold">{t}</p>
                <p className="text-[9px] text-[#26342E]/55 mt-0.5">{v}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Screen>
  ),

  "form-therapy": (mode) => (
    <Screen>
      <MNav mode={mode} />
      <div className="p-4 sm:p-6 max-w-md">
        <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-[#B66E50] flex items-center gap-1.5"><Lock size={9} /> Private enquiry</p>
        <p className="font-serif text-xl font-light mt-1.5">A calm first step</p>
        <div className="space-y-2.5 mt-4">
          <Field label="The kind of support" hint="Choose an area, it's fine to be unsure" />
          <Field label="Your name" hint="However you'd like to be addressed" />
          <Field label="Preferred contact" hint="Email or phone, whichever feels easier" />
          <Field label="Availability" hint="Days or times that suit you" />
        </div>
        <p className="text-[8px] text-[#26342E]/50 mt-3 leading-relaxed">
          Routing information only, no clinical history is asked for here. A secure intake can follow later.
        </p>
        <div className="mt-3.5"><MBtn tone="clay">Start privately</MBtn></div>
      </div>
    </Screen>
  ),

  "form-school": (mode) => (
    <Screen>
      <MNav mode={mode} />
      <div className="p-4 sm:p-6 max-w-md">
        <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-[#6F806E]">Schools and organisations</p>
        <p className="font-serif text-xl font-light mt-1.5">Request a consultation</p>
        <div className="space-y-2.5 mt-4">
          <Field label="Organisation" hint="School or organisation name" />
          <Field label="The challenge" hint="Learner wellbeing, parent support, staff capability…" />
          <Field label="Programme interest" hint="If you already have one in mind" />
          <Field label="Contact" hint="Name, role and email" />
        </div>
        <p className="text-[8px] text-[#26342E]/50 mt-3">Response expectation: within two working days.</p>
        <div className="mt-3.5"><MBtn>Request details</MBtn></div>
      </div>
    </Screen>
  ),

  "form-training": (mode) => (
    <Screen>
      <MNav mode={mode} />
      <div className="p-4 sm:p-6 max-w-md">
        <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-[#C29A4A]">Training enquiry</p>
        <p className="font-serif text-xl font-light mt-1.5">Decision-ready, in one exchange</p>
        <div className="space-y-2.5 mt-4">
          <Field label="Programme" hint="Select a programme or cohort" />
          <Field label="Preferred dates" hint="Upcoming intake or a future one" />
          <Field label="Requirements" hint="We confirm eligibility and materials" />
          <Field label="Contact" hint="Name and email" />
        </div>
        <div className="mt-3.5"><MBtn>Request details</MBtn></div>
      </div>
    </Screen>
  ),
};

/* ---------- the section ---------- */

export default function ConceptScreens() {
  const [mode, setMode] = useState("desktop");
  const [active, setActive] = useState(CONCEPT_SCREENS[0]);

  return (
    <Section id="concept" theme="sage" className="py-16 sm:py-24 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <AnimatePresence mode="sync">
          <motion.img
            key={active.id}
            src={IMAGES[active.img]}
            alt=""
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.22 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-[#E6E8DE]/80" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#E6E8DE] via-transparent to-[#E6E8DE]" />
      </div>
      <Wrap className="relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <SectionHead
            eyebrow="The future website concept"
            title="Not described. Demonstrated."
            intro="Eleven working concept screens from the proposed platform, rendered live. Switch between desktop and mobile; the same care, at every size."
          />
          <Reveal delay={2} className="shrink-0">
            <div data-testid="viewport-switcher" className="inline-flex bg-[#26342E] rounded-full p-1.5 gap-1" role="tablist" aria-label="Viewport">
              {[
                { id: "desktop", icon: Monitor, label: "Desktop" },
                { id: "mobile", icon: Smartphone, label: "Mobile" },
              ].map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  data-testid={`viewport-${id}`}
                  role="tab"
                  aria-selected={mode === id}
                  onClick={() => setMode(id)}
                  className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] px-5 py-2.5 rounded-full transition-[background-color,color] duration-400 cursor-pointer ${
                    mode === id ? "bg-[#F6F0E6] text-[#26342E]" : "text-[#F6F0E6]/55 hover:text-[#F6F0E6]"
                  }`}
                >
                  <Icon size={13} />
                  {label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 mt-10 sm:mt-12">
          <div className="lg:col-span-4 order-2 lg:order-1 min-w-0">
            <div className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-3 lg:pb-0 -mx-6 px-6 lg:mx-0 lg:px-0" role="listbox" aria-label="Concept screens">
              {CONCEPT_SCREENS.map((s) => {
                const on = active.id === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={`screen-tab-${s.id}`}
                    role="option"
                    aria-selected={on}
                    onClick={() => setActive(s)}
                    className={`shrink-0 lg:shrink text-left rounded-xl px-4 py-3 transition-[background-color,border-color] duration-300 border cursor-pointer backdrop-blur-sm ${
                      on ? "bg-[#26342E] border-[#26342E] text-[#F6F0E6]" : "bg-[#F6F0E6]/85 border-[#26342E]/10 text-[#26342E]/80 hover:border-[#26342E]/40 hover:bg-[#F6F0E6]"
                    }`}
                  >
                    <span className={`text-[9px] font-bold tracking-[0.2em] ${on ? "text-[#C29A4A]" : "text-[#26342E]/40"}`}>{s.num}</span>
                    <span className="block text-sm font-semibold mt-0.5 whitespace-nowrap lg:whitespace-normal">{s.title}</span>
                  </button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              <motion.p
                key={active.id}
                data-testid="screen-description"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="hidden lg:block text-sm leading-relaxed text-[#26342E]/75 mt-8 bg-[#F6F0E6]/75 backdrop-blur-sm rounded-r-2xl border-l-2 border-[#B66E50] py-4 pr-5 pl-5"
              >
                <span className="block text-[9px] font-bold uppercase tracking-[0.2em] text-[#B66E50] mb-2">{active.badge}</span>
                {active.desc}
              </motion.p>
            </AnimatePresence>
          </div>

          <div className="lg:col-span-8 order-1 lg:order-2 min-w-0">
            <Reveal>
              <div data-testid="concept-frame" className={`transition-[max-width] duration-700 mx-auto ${mode === "mobile" ? "max-w-[380px]" : "max-w-full"}`}>
                {mode === "desktop" ? (
                  <div className="rounded-2xl overflow-hidden shadow-[0_40px_80px_-30px_rgba(38,52,46,0.45)] border border-[#26342E]/15 bg-white">
                    <div className="flex items-center gap-3 px-4 py-2.5 bg-[#EFEAE0] border-b border-[#26342E]/10">
                      <span className="flex gap-1.5">
                        <i className="w-2.5 h-2.5 rounded-full bg-[#B66E50]/70 block" />
                        <i className="w-2.5 h-2.5 rounded-full bg-[#C29A4A]/70 block" />
                        <i className="w-2.5 h-2.5 rounded-full bg-[#6F806E]/70 block" />
                      </span>
                      <span className="flex-1 text-center text-[9px] font-medium tracking-wide text-[#26342E]/50 bg-white/70 rounded-full py-1 px-4 max-w-[260px] mx-auto">
                        yourorganisation.co.za
                      </span>
                      <span className="w-10" />
                    </div>
                    <div className="h-[480px] overflow-hidden">
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={`${active.id}-desktop`}
                          initial={{ opacity: 0, scale: 0.985 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 1.01 }}
                          transition={{ duration: 0.45, ease: EASE }}
                          className="h-full"
                        >
                          {SCREENS[active.id]("desktop")}
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="relative rounded-[3.2rem] bg-gradient-to-b from-[#3a3a3c] via-[#1c1c1e] to-[#2c2c2e] p-[9px] shadow-[0_60px_100px_-28px_rgba(20,25,22,0.6)]">
                      <span className="absolute -left-[2px] top-28 w-[3px] h-8 bg-[#2c2c2e] rounded-l-md" aria-hidden="true" />
                      <span className="absolute -left-[2px] top-[10.5rem] w-[3px] h-12 bg-[#2c2c2e] rounded-l-md" aria-hidden="true" />
                      <span className="absolute -right-[2px] top-36 w-[3px] h-16 bg-[#2c2c2e] rounded-r-md" aria-hidden="true" />
                      <div className="rounded-[2.6rem] overflow-hidden bg-[#F6F0E6] relative">
                        <div className="relative flex items-center justify-between px-7 pt-3 pb-1.5 bg-[#F6F0E6]">
                          <span className="text-[10px] font-semibold text-[#26342E]">9:41</span>
                          <span className="absolute left-1/2 -translate-x-1/2 top-2 w-[74px] h-[21px] bg-black rounded-full" aria-hidden="true" />
                          <span className="flex items-end gap-1.5" aria-hidden="true">
                            <span className="flex items-end gap-[2px]">
                              <i className="w-[2.5px] h-[4px] bg-[#26342E] rounded-[1px] block" />
                              <i className="w-[2.5px] h-[6px] bg-[#26342E] rounded-[1px] block" />
                              <i className="w-[2.5px] h-[8px] bg-[#26342E] rounded-[1px] block" />
                              <i className="w-[2.5px] h-[10px] bg-[#26342E] rounded-[1px] block" />
                            </span>
                            <svg width="14" height="10" viewBox="0 0 14 10" fill="none" className="text-[#26342E]">
                              <path d="M7 8.6a1.2 1.2 0 100 .01M3.4 6.2a5.2 5.2 0 017.2 0M1 3.8a8.6 8.6 0 0112 0" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                            </svg>
                            <span className="flex items-center">
                              <i className="w-[18px] h-[9px] rounded-[2.5px] border border-[#26342E]/60 block relative">
                                <i className="absolute inset-[1.5px] right-[4px] bg-[#26342E] rounded-[1px] block" />
                              </i>
                              <i className="w-[1.5px] h-[4px] bg-[#26342E]/60 rounded-r block ml-[1px]" />
                            </span>
                          </span>
                        </div>
                        <div className="h-[516px] overflow-hidden">
                          <AnimatePresence mode="wait">
                            <motion.div
                              key={`${active.id}-mobile`}
                              initial={{ opacity: 0, scale: 0.985 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 1.01 }}
                              transition={{ duration: 0.45, ease: EASE }}
                              className="h-full"
                            >
                              {SCREENS[active.id]("mobile")}
                            </motion.div>
                          </AnimatePresence>
                        </div>
                        <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-24 h-1 bg-[#26342E]/30 rounded-full" aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <p className="lg:hidden text-xs leading-relaxed text-[#26342E]/65 mt-6 border-l-2 border-[#B66E50] pl-4">
                <span className="block text-[9px] font-bold uppercase tracking-[0.2em] text-[#B66E50] mb-1.5">{active.badge}</span>
                {active.desc}
              </p>
            </Reveal>
          </div>
        </div>
      </Wrap>
    </Section>
  );
}
